import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { PagesStore } from 'src/app/core/utils/pages-store/pages-store';
import {
  ApiPagesStoreRequestData,
  PagesStoreRequestData,
} from 'src/app/core/utils/pages-store/pages-store-request-data';
import { PagesStoreResponse } from 'src/app/core/utils/pages-store/pages-store-response';
import { CheckboxChildrenValue, FiltersData } from 'src/app/shared/models/filters';
import { environment } from 'src/environments/environment';
import { IMSPCompanyContactFilter } from '../../models/msp-company-contact';
import { IMSPPriotityFilter } from '../../models/msp-priority';
import { ManagedServicesRepository } from '../../services/managed-services-repository.service';
import { HubMSPSortColumn } from '../models/hub-msp-sort-column';
import { TicketHeader } from '../models/ticket-header';
import { pendingTriageStatuses } from '../types/msp-statuses-map';
import { valueAsOrdersSortColumn } from '../utils/tickets-table-utils';
import { ManagedServicesSharedDataService } from '../../services/managed-services-shared-data.service';

@Component({
  selector: 'tickets-table',
  templateUrl: './tickets-table.component.html',
  styleUrls: ['./tickets-table.component.scss'],
  standalone: false,
})
export class TicketsTableComponent implements OnInit, OnDestroy {
  store: PagesStore<TicketHeader>;

  /** The index of the currently selected page */
  pageIndex: number = 0;

  /** The datasource for which tickets will be displayed */
  dataSource: TicketHeader[] = [];

  /** The number of tickets to be displayed */
  numberOfTickets: number = 0;

  /** The columns to be displayed in the table */
  readonly tableColumns = [
    'id',
    'statusName',
    'summary',
    'priorityName',
    'relationship',
    'dateEntered',
    'lastUpdated',
    'dateClosed',
    'boardName',
    'businessUnitName',
    'contactFullName',
  ];

  /** The size of the pages for the table */
  readonly pageSize: number = 10;

  /** Amount of pages to be fetched per HTTP request */
  readonly pagesPerRequest: number = 2;

  /** The direction by which to sort the data */
  private sortDirection: string = 'DESC';

  /** The column by which to sort the orders */
  private sortColumn: HubMSPSortColumn = 'dateEntered';

  /** Array to hold the MSP priorities */
  mspPriorities: IMSPPriotityFilter[] = [];

  /** Array to hold the MSP company contacts */
  mspCompanyContacts: IMSPCompanyContactFilter[] = [];

  /** Flag to show or hide the filters **/
  showFilters: boolean = false;

  /** Fiters data */
  filtersData: FiltersData;

  /** Request boards for category filter */
  requestBoards: CheckboxChildrenValue[] = [
    {
      id: 'Help Desk',
      name: 'Help Desk',
      visible: true,
      completed: false,
      indeterminate: false,
    },
    {
      id: 'Change Management',
      name: 'Change Management',
      visible: true,
      completed: false,
      indeterminate: false,
    },
    {
      id: 'Request Fulfillment',
      name: 'Request Fulfillment',
      visible: true,
      completed: false,
      indeterminate: false,
    },
  ];

  requestData: Record<string, string> = {};

  get isFetchingTickets(): boolean {
    return this.store.isFetchingData;
  }

  /** Subscriptions collection for cleanup */
  private subscriptions = new Subscription();

  constructor(
    private http: HttpClient,
    private managedServiceRepository: ManagedServicesRepository,
    private userDataService: UserDataService,
    private route: ActivatedRoute,
    private managedServicesSharedDataService: ManagedServicesSharedDataService,
    private permissionsService: PermissionsService,
  ) {
    this.store = new PagesStore(
      `${environment.restUrl}/v1/msp/tickets`,
      'GET',
      this.http,
      5,
      this.mapHeaders,
      this.pagesPerRequest,
      undefined,
      this.mapPropertiesFn,
    );
  }

  ngOnInit(): void {
    const currentParams = this.route.snapshot.queryParams;

    // Subscribe to future query parameter changes
    const routeSubscription = this.route.queryParams.subscribe((params) => {
      if (params['refresh'] && params['refresh'] !== currentParams['refresh']) {
        this.refreshTickets();
      }
    });

    this.subscriptions.add(routeSubscription);

    forkJoin({
      mspPriorities: this.managedServiceRepository.getMspPriorities(),
      mspCompanyContacts: this.managedServiceRepository.getMspTicketsCompanyContacts(),
      mspAccountBoards: this.managedServiceRepository.getMspAccountBoards(),
    }).subscribe(({ mspPriorities, mspCompanyContacts, mspAccountBoards }) => {
      this.mspPriorities = mspPriorities.map((priority) => {
        return {
          id: priority.id,
          name: priority.name,
          visible: true,
          completed: false,
          indeterminate: false,
        };
      });

      this.mspCompanyContacts = mspCompanyContacts
        .map((contact) => {
          return {
            id: contact.id,
            name: `${contact.firstName ?? ''} ${contact.lastName ?? ''}`,
            caption: contact.email,
            visible: true,
            completed: false,
            indeterminate: false,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      this.buildFiltersData();
      this.showFilters = true;

      if (mspAccountBoards.length > 0) {
        mspAccountBoards.forEach((el) => {
          this.requestBoards.push({
            id: el.name,
            name: el.name,
            visible: true,
            completed: false,
            indeterminate: false,
          });
        });
      }
    });

    // Subscribe to the currentPageChanges to update the dataSource
    this.store.currentPageChanges.subscribe((currentPageData) => {
      this.dataSource = currentPageData.data;

      this.numberOfTickets = currentPageData.count;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  buildFiltersData() {
    const hasPowerPermissions = this.permissionsService.hasPermission('managed-services', 'Tickets', 'edit');

    this.filtersData = {
      title: 'Tickets Filters',
      headerFilters: [
        {
          type: 'text',
          panelName: 'Ticket ID',
          key: 'id',
          inputValue: '',
        },
        {
          type: 'buttonGroup',
          panelName: 'Statuses',
          key: 'statusesGroup',
          inputValue: [
            { name: 'open', selected: true },
            { name: 'waiting', selected: false },
            { name: 'closed', selected: false },
          ],
        },
      ],
      bodyFilters: [
        {
          type: 'text',
          panelName: 'Summary',
          key: 'summary',
          inputValue: '',
        },
        {
          type: 'text',
          panelName: 'Business Unit',
          key: 'businessUnitNames',
          inputValue: '',
        },
        {
          type: 'checkboxes',
          panelName: 'Priority',
          key: 'priorityIds',
          allowFiltering: false,
          inputValue: {
            name: 'Select all',
            filteredValue: '',
            allowTree: true,
            completed: false,
            indeterminate: false,
            children: this.mspPriorities
              ? [
                  ...this.mspPriorities,
                  {
                    id: 'pending-triage',
                    name: 'Pending Triage',
                    visible: true,
                    completed: false,
                    indeterminate: false,
                  },
                ]
              : [],
          },
        },
        {
          type: 'dateRange',
          panelName: 'Creation',
          key: 'creation',
          allowDateShortcut: false,
          inputValue: { startDate: null, endDate: null },
        },
        {
          type: 'buttonGroup',
          panelName: 'Ticket Relation',
          key: 'relationship',
          inputValue: [
            { name: 'parent', selected: true },
            { name: 'child', selected: false },
          ],
        },
        {
          type: 'dateRange',
          panelName: 'Updated',
          key: 'updated',
          allowDateShortcut: false,
          inputValue: { startDate: null, endDate: null },
        },
        {
          type: 'dateRange',
          panelName: 'Closed',
          key: 'closed',
          allowDateShortcut: false,
          inputValue: { startDate: null, endDate: null },
        },
        {
          type: 'checkboxes',
          panelName: 'Board',
          key: 'boardNames',
          allowFiltering: false,
          inputValue: {
            name: 'Select all',
            filteredValue: '',
            allowTree: true,
            completed: false,
            indeterminate: false,
            children: [
              {
                name: 'Request',
                visible: true,
                completed: false,
                indeterminate: false,
                children: this.requestBoards,
              },
              {
                name: 'Incident',
                visible: true,
                completed: false,
                indeterminate: false,
                children: [
                  {
                    id: 'Global NOC',
                    name: 'Global NOC',
                    visible: true,
                    completed: false,
                    indeterminate: false,
                  },
                  {
                    id: 'Global SOC',
                    name: 'Global SOC',
                    visible: true,
                    completed: false,
                    indeterminate: false,
                  },
                  {
                    id: 'NOC-RMM',
                    name: 'NOC-RMM',
                    visible: true,
                    completed: false,
                    indeterminate: false,
                  },
                  {
                    id: 'SOC-RMM',
                    name: 'SOC-RMM',
                    visible: true,
                    completed: false,
                    indeterminate: false,
                  },
                  {
                    id: 'Problem Management',
                    name: 'Problem Management',
                    visible: true,
                    completed: false,
                    indeterminate: false,
                  },
                  {
                    id: 'SOC',
                    name: 'SOC',
                    visible: true,
                    completed: false,
                    indeterminate: false,
                  },
                ],
              },
            ],
          },
        },
      ],
    };

    if (hasPowerPermissions) {
      this.filtersData.bodyFilters.push({
        type: 'checkboxes',
        panelName: 'Contacts',
        key: 'contactIds',
        allowFiltering: true,
        inputValue: {
          name: 'Select all',
          filteredValue: '',
          allowTree: true,
          completed: false,
          indeterminate: false,
          children: this.mspCompanyContacts ?? [],
        },
      });
    }

    // Initialize default filter values for selected filters
    const defaultFilterValues: Record<string, any> = {};

    // Initialize default status filter from statusesGroup
    if (this.filtersData.headerFilters) {
      const statusesGroup = this.filtersData.headerFilters.find((filter) => filter.key === 'statusesGroup');
      if (statusesGroup && Array.isArray(statusesGroup.inputValue)) {
        const selectedStatuses = statusesGroup.inputValue
          .filter((status: { name: string; selected: boolean }) => status.selected)
          .map((status: { name: string; selected: boolean }) => status.name);

        if (selectedStatuses.length > 0) {
          defaultFilterValues['statusesGroup'] = selectedStatuses;
        }
      }
    }

    if (this.filtersData.bodyFilters) {
      const relationshipFilter = this.filtersData.bodyFilters.find((filter) => filter.key === 'relationship');
      if (relationshipFilter && Array.isArray(relationshipFilter.inputValue)) {
        const selectedRelationship = relationshipFilter.inputValue
          .filter((relationship: { name: string; selected: boolean }) => relationship.selected)
          .map((relationship: { name: string; selected: boolean }) => relationship.name);

        if (selectedRelationship.length > 0) {
          defaultFilterValues['relationship'] = selectedRelationship;
        }
      }
    }

    if (Object.keys(this.managedServicesSharedDataService.getFilters()).length > 0) {
      this.getFiltersValues(this.managedServicesSharedDataService.getFilters());
    } else {
      this.getFiltersValues(defaultFilterValues);
    }
  }

  /** Called for event on the MatSort */
  onSortEvent(sortEvent: Sort) {
    const direction = sortEvent.direction === 'asc' ? 'ASC' : 'DESC';
    const sortColumn = valueAsOrdersSortColumn(sortEvent.active);

    // Do nothing if there are no direction or sortColumn changes
    if (direction === this.sortDirection && sortColumn === this.sortColumn) {
      return;
    }

    this.sortDirection = direction;
    this.sortColumn = sortColumn;
    this.pageIndex = 0;
    this.fetchTickets();
  }

  /** Called when there is an event on the paginator */
  onPageChange(pageEvent: PageEvent) {
    this.pageIndex = pageEvent.pageIndex;
    this.fetchTickets();
  }

  fetchTickets() {
    this.store.requestPage(this.buildRequestData());
  }

  getFiltersValues(values: Record<string, any>) {
    if (Object.keys(values).length === 0) {
      this.requestData = {};
    } else {
      // Clone the values object
      const filterValues = { ...values };

      // Convert the array values to a string separated by a semicolon
      for (const prop in filterValues) {
        if (Array.isArray(filterValues[prop])) {
          // Special handling for relationship filter
          if (prop === 'relationship') {
            const relationshipValues = filterValues[prop];
            if (relationshipValues.includes('parent') && relationshipValues.includes('child')) {
              filterValues[prop] = 'both';
            } else {
              filterValues[prop] = relationshipValues.join(';');
            }
          } else {
            filterValues[prop] = filterValues[prop].map((value) => (value === null ? 'null' : value)).join(';');
          }
        }

        if (prop === 'creation') {
          filterValues['createdAfter'] = filterValues[prop].startDate;
          filterValues['createdBefore'] = filterValues[prop].endDate;

          delete filterValues[prop];
        } else if (prop === 'updated') {
          filterValues['updatedAfter'] = filterValues[prop].startDate;
          filterValues['updatedBefore'] = filterValues[prop].endDate;

          delete filterValues[prop];
        } else if (prop === 'closed') {
          filterValues['closedAfter'] = filterValues[prop].startDate;
          filterValues['closedBefore'] = filterValues[prop].endDate;

          delete filterValues[prop];
        } else if (prop === 'priorityIds') {
          const isPendingTriage = filterValues['priorityIds']?.split(';').includes('pending-triage');
          filterValues['priorityIds'] = filterValues['priorityIds']
            ?.split(';')
            .filter((value) => value !== 'pending-triage')
            .join(';');

          if (filterValues['priorityIds'] === '') {
            delete filterValues['priorityIds'];
          }
          if (isPendingTriage) {
            filterValues['pendingTriage'] = true;
          }
        }
      }

      this.requestData = {
        ...filterValues,
      };
    }

    // Reset the pageIndex to 0 and fetch the tickets
    this.pageIndex = 0;
    this.fetchTickets();
  }

  private mapHeaders(rawResponse: PagesStoreResponse<any>): PagesStoreResponse<any> {
    return {
      data: rawResponse.data,
      count: rawResponse.count,
    };
  }

  private mapPropertiesFn(object: ApiPagesStoreRequestData): { [key: string]: any } {
    const top = object.pageSize;
    const skip = object.pageOffset;
    const { sortColumn, sortDirection } = object;
    const orderBy = `${sortColumn} ${sortDirection}`;

    //delete the field with key equals to pageSize, pageIndex, sortColumn, sortDirection, pageOffset
    const newRequestData = { ...object };

    delete newRequestData.pageSize;
    delete newRequestData.pageIndex;
    delete newRequestData.sortColumn;
    delete newRequestData.sortDirection;
    delete newRequestData.pageOffset;

    return {
      ...newRequestData,
      $top: top,
      $skip: skip,
      $orderBy: orderBy,
    };
  }

  private buildRequestData(): PagesStoreRequestData {
    return {
      ...this.requestData,
      pageSize: this.pageSize,
      pageIndex: this.pageIndex,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection === 'ASC' ? 'ASC' : 'DESC',
    };
  }

  isPendingTriagePriority(item: TicketHeader) {
    return pendingTriageStatuses.includes(item.statusName as any);
  }

  /**
   * Force a refresh of tickets data by triggering a new request
   * after clearing the cache
   */
  refreshTickets() {
    // Clear the internal cachedPages Map
    this.store.refreshCachedData();

    if (Object.keys(this.managedServicesSharedDataService.getFilters()).length > 0) {
      this.getFiltersValues(this.managedServicesSharedDataService.getFilters());
    } else {
      this.fetchTickets();
    }
  }
}
